<template>
  <header 
    class="site-header" 
    :class="{ 'site-header--compact': isCompact, 'site-header--menu-open': isMobileMenuOpen }"
  >
    <div class="site-header__container">
      <!-- Logo -->
      <div class="site-header__logo">
        <a 
          href="/" 
          class="site-header__logo-link"
          :aria-label="$t('navigation.home', 'Trails of Hooves Home')"
        >
          <img 
            v-if="logoUrl"
            :src="logoUrl"
            alt="Trails of Hooves"
            class="site-header__logo-image"
          />
          <span v-else class="site-header__logo-text">
            <span class="site-header__logo-text--trails">Trails of</span>
            <span class="site-header__logo-text--hooves">Hooves</span>
          </span>
        </a>
      </div>

      <!-- Mobile Menu Toggle -->
      <button
        class="site-header__mobile-toggle"
        :class="{ 'site-header__mobile-toggle--active': isMobileMenuOpen }"
        @click="toggleMobileMenu"
        :aria-expanded="isMobileMenuOpen"
        :aria-label="$t('navigation.menu', 'Toggle navigation menu')"
        aria-controls="main-navigation"
        type="button"
      >
        <span class="site-header__mobile-toggle-bar"></span>
        <span class="site-header__mobile-toggle-bar"></span>
        <span class="site-header__mobile-toggle-bar"></span>
      </button>

      <!-- Navigation -->
      <nav 
        class="site-header__nav" 
        :class="{ 'site-header__nav--open': isMobileMenuOpen }"
        aria-label="Main navigation"
        id="main-navigation"
        @keydown.esc="closeDropdowns(); closeMobileMenu()"
      >
        <ul class="site-header__nav-list">
          <!-- Loading State -->
          <li v-if="isLoading" class="site-header__nav-item">
            <span class="site-header__nav-link">Loading...</span>
          </li>
          
          <!-- Error State -->
          <li v-else-if="hasError" class="site-header__nav-item">
            <span class="site-header__nav-link">Menu unavailable</span>
          </li>
          
          <!-- Navigation Items -->
          <template v-else>
            <li 
              v-for="(item, index) in navigationItems" 
              :key="item.url ?? item.title ?? `nav-${index}`"
              class="site-header__nav-item"
              :class="{ 'site-header__nav-item--has-dropdown': item.dropdownItems && item.dropdownItems.length > 0 }"
            >
              <!-- Main nav link (with or without dropdown) -->
              <a 
                v-if="item.url && !item.dropdownItems?.length"
                :href="item.url"
                class="site-header__nav-link"
                @click="closeMobileMenu"
              >
                {{ item.title }}
              </a>
              
              <!-- Dropdown toggle button -->
              <button
                v-else-if="item.dropdownItems?.length"
                :id="`dropdown-button-${index}`"
                class="site-header__nav-link site-header__nav-link--dropdown"
                @click="toggleDropdown(index)"
                @keydown.esc="closeDropdowns()"
                :aria-expanded="activeDropdown === index"
                aria-haspopup="menu"
                :aria-controls="`dropdown-${index}`"
                :aria-label="`${item.title} menu`"
                type="button"
              >
                {{ item.title }}
                <svg 
                  class="site-header__dropdown-icon"
                  :class="{ 'site-header__dropdown-icon--open': activeDropdown === index }"
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>

              <!-- Dropdown menu -->
              <ul 
                v-if="item.dropdownItems?.length"
                :id="`dropdown-${index}`"
                class="site-header__dropdown"
                :class="{ 'site-header__dropdown--open': activeDropdown === index }"
              >
                <li 
                  v-for="(dropdownItem, dropdownIndex) in item.dropdownItems"
                  :key="dropdownItem.url ?? `dd-${dropdownIndex}`"
                  class="site-header__dropdown-item"
                >
                  <a 
                    :href="dropdownItem.url"
                    class="site-header__dropdown-link"
                    @click="closeDropdowns(); closeMobileMenu()"
                  >
                    {{ dropdownItem.title }}
                  </a>
                </li>
              </ul>
            </li>
          </template>
        </ul>
        
        <!-- Language Switcher -->
        <div class="site-header__language">
          <LanguageSwitcher />
        </div>
      </nav>
    </div>

    <!-- Mobile Menu Panel -->
    <transition name="mobile-slide">
      <aside
        v-if="isMobileMenuOpen"
        class="site-header__mobile-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Mobile menu"
      >
        <nav class="site-header__mobile-nav" aria-label="Mobile navigation">
          <ul class="site-header__mobile-list">
            <li
              v-for="(item, index) in navigationItems"
              :key="item.url ?? item.title ?? `m-${index}`"
              class="site-header__mobile-item"
            >
              <!-- Simple link -->
              <a
                v-if="item.url && !item.dropdownItems?.length"
                :href="item.url"
                class="site-header__mobile-link"
                @click="closeMobileMenu"
              >
                {{ item.title }}
              </a>

              <!-- Group with subitems -->
              <div v-else class="site-header__mobile-group">
                <button
                  class="site-header__mobile-group-toggle"
                  :aria-expanded="activeDropdown === index"
                  :aria-controls="`m-sub-${index}`"
                  type="button"
                  @click="toggleDropdown(index)"
                >
                  <span class="site-header__mobile-group-title">{{ item.title }}</span>
                  <svg
                    class="site-header__mobile-group-icon"
                    :class="{ 'site-header__mobile-group-icon--open': activeDropdown === index }"
                    width="16" height="16" viewBox="0 0 16 16" fill="none"
                    aria-hidden="true"
                  >
                    <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <ul
                  :id="`m-sub-${index}`"
                  class="site-header__mobile-sublist"
                  :class="{ 'site-header__mobile-sublist--open': activeDropdown === index }"
                >
                  <li
                    v-for="(dropdownItem, dIndex) in item.dropdownItems"
                    :key="dropdownItem.url ?? `ms-${index}-${dIndex}`"
                    class="site-header__mobile-subitem"
                  >
                    <a
                      :href="dropdownItem.url"
                      class="site-header__mobile-sublink"
                      @click="closeDropdowns(); closeMobileMenu()"
                    >
                      {{ dropdownItem.title }}
                    </a>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </nav>
      </aside>
    </transition>

    <!-- Overlay for mobile menu -->
    <div 
      v-if="isMobileMenuOpen"
      class="site-header__overlay"
      @click="closeMobileMenu"
      aria-hidden="true"
    ></div>
  </header>
</template>

<script setup lang="ts">
import useHeader from './Header'
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher.vue'

// Name for better devtools DX
defineOptions({ name: 'SiteHeader' })

// Use the extracted header logic
const {
  // State
  isCompact,
  isMobileMenuOpen,
  activeDropdown,
  isLoading,
  hasError,
  navigationItems,
  logoUrl,
  
  // Methods
  toggleMobileMenu,
  closeMobileMenu,
  toggleDropdown,
  closeDropdowns,
  
  // Internationalization
  $t
} = useHeader()

// Expose methods for parent components if needed
defineExpose({
  closeMobileMenu,
  closeDropdowns
})
</script>

<style lang="scss" scoped>
@use './Header.scss';
</style>
